import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./AgentHome.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const FOX_SPRITE_A = [
  "r........r...",
  "rr......rr...",
  "rrr....rrr...",
  "rrrrrrrrrr...",
  "rrrrrrrrrr.ww",
  "r.rrrr.rrr.rr",
  "rrrrrrrrrrrrr",
  "rrww..wwrrrrr",
  "rrwwwwwwrrrrr",
  ".d.d..d.d....",
  ".d....d......",
];

const FOX_SPRITE_B = [
  "r........r...",
  "rr......rr...",
  "rrr....rrr...",
  "rrrrrrrrrr...",
  "rrrrrrrrrr.ww",
  "r.rrrr.rrr.rr",
  "rrrrrrrrrrrrr",
  "rrww..wwrrrrr",
  "rrwwwwwwrrrrr",
  ".d.d..d.d....",
  "...d....d....",
];

function FoxGrid({ hidden = false }) {
  const wrapRef = useRef(null);
  const colsRef = useRef(42);
  const hiddenRef = useRef(hidden);
  const [cols, setCols] = useState(42);
  const [step, setStep] = useState({ x: 2, dir: 1, frame: 0 });
  const rows = FOX_SPRITE_A.length;
  const spriteW = FOX_SPRITE_A[0].length;

  hiddenRef.current = hidden;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const inner = Math.max(0, el.clientWidth - 16);
      const next = Math.max(24, Math.round((inner + 2) / 9));
      if (next === colsRef.current) return;
      colsRef.current = next;
      setCols(next);
      setStep((prev) => {
        const minX = -spriteW + 2;
        const maxX = Math.max(minX, next - spriteW - 1);
        return { ...prev, x: Math.min(Math.max(prev.x, minX), maxX) };
      });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [spriteW]);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const id = window.setInterval(() => {
      if (hiddenRef.current) return;
      setStep((prev) => {
        const minX = -spriteW + 2;
        const maxX = Math.max(minX, colsRef.current - spriteW - 1);
        let nextX = prev.x + prev.dir;
        let nextDir = prev.dir;
        if (nextX >= maxX) nextDir = -1;
        else if (nextX <= minX) nextDir = 1;
        return { x: nextX, dir: nextDir, frame: (prev.frame + 1) % 2 };
      });
    }, 180);
    return () => window.clearInterval(id);
  }, [spriteW]);

  const sprite = step.frame === 0 ? FOX_SPRITE_A : FOX_SPRITE_B;
  const cells = [];
  for (let y = 0; y < rows; y += 1) {
    const row = sprite[y];
    for (let x = 0; x < cols; x += 1) {
      const local = x - step.x;
      const sx = step.dir === -1 ? local : spriteW - 1 - local;
      const cell = sx >= 0 && sx < spriteW ? row[sx] : ".";
      cells.push(
        <span key={`${x}-${y}`} className={`ah-fox-cell ah-fox-cell--${cell}`} />
      );
    }
  }
  return (
    <div
      ref={wrapRef}
      className={`ah-ask-fox${hidden ? " is-tucked" : ""}`}
      style={{ "--fox-cols": cols }}
      aria-hidden="true"
    >
      {cells}
    </div>
  );
}

const GREET_ASK =
  "Hey, this is Young. Welcome to my portfolio. How are you doing today?";
const GREET_AUDIO =
  process.env.REACT_APP_GREET_AUDIO ||
  "https://res.cloudinary.com/dlzvz9qj8/video/upload/v1792711623/Audio_Recording_-_Oct_22_2026_w1wdw0.mp3";

const ROLE_CHIPS = [
  "I'm hiring / recruiting",
  "Product or UX designer",
  "Engineering or PM",
  "Student or career switcher",
];

const PURPOSE_CHIPS = [
  "Considering Young for a role",
  "Learning from her case studies",
  "Just exploring the site",
];

function buildVisitorContext({ name, role, purpose }) {
  const parts = [];
  if (name) parts.push(`Visitor's name: ${name}.`);
  if (role) parts.push(`Visitor's role or background: ${role}.`);
  if (purpose) parts.push(`Why they're exploring the portfolio: ${purpose}.`);
  return parts.join(" ");
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function typingPause(text) {
  const len = String(text || "").length;
  return Math.min(1400, Math.max(520, 320 + len * 12));
}

async function deliverAssistantBubbles(data, { setMessages, setTaijiHighlight, taijiTimerRef }) {
  const parts =
    Array.isArray(data.spoken_messages) && data.spoken_messages.length
      ? data.spoken_messages
      : [data.spoken_answer || data.caption].filter(Boolean);
  const showTaiji = Boolean(data.show_taiji);
  if (showTaiji) {
    setTaijiHighlight(true);
    if (taijiTimerRef.current) window.clearTimeout(taijiTimerRef.current);
    taijiTimerRef.current = window.setTimeout(() => setTaijiHighlight(false), 14000);
  }

  for (let i = 0; i < parts.length; i += 1) {
    if (i > 0) {
      await wait(typingPause(parts[i]));
    }
    const isLast = i === parts.length - 1;
    playPop();
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: parts[i],
        evidence: isLast ? data.evidence || [] : [],
        showTaiji: isLast && showTaiji,
        pop: true,
      },
    ]);
  }
}

const DEFAULT_CHIP_EXCLUDE =
  /how many years|senior product designer|can you lead a team/i;

function filterDefaultChips(list) {
  return (list || []).filter((q) => !DEFAULT_CHIP_EXCLUDE.test(q));
}

/** 1st heart+shake, 2–3 like, 4–6 none, then one react every 4 turns (like/heart alternate). */
function reactionForUserTurn(n) {
  if (n === 1) return { type: "heart", shake: true };
  if (n === 2 || n === 3) return { type: "like" };
  if (n >= 4 && n <= 6) return null;
  if (n >= 7 && (n - 7) % 4 === 0) {
    const cycle = Math.floor((n - 7) / 4);
    return cycle % 2 === 0 ? { type: "like" } : { type: "heart", shake: true };
  }
  return null;
}

async function resolveVisitorName(text, assistantCue, messageList) {
  const cue =
    assistantCue ||
    [...(messageList || [])].reverse().find((m) => m.role === "assistant")?.content ||
    GREET_ASK;
  try {
    const res = await fetch(`${API_URL}/api/onboarding/parse`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, assistantCue: cue }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.kind === "name" && data.name) return data.name;
  } catch {
    /* offline — no name */
  }
  return null;
}

function prettyName(name) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function playPop() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const t = ctx.currentTime;
    const pop = ctx.createOscillator();
    const click = ctx.createOscillator();
    const gain = ctx.createGain();
    pop.type = "sine";
    click.type = "triangle";
    pop.frequency.setValueAtTime(640, t);
    pop.frequency.exponentialRampToValueAtTime(210, t + 0.12);
    click.frequency.setValueAtTime(980, t);
    click.frequency.exponentialRampToValueAtTime(280, t + 0.09);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.2, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    pop.connect(gain);
    click.connect(gain);
    gain.connect(ctx.destination);
    pop.start(t);
    click.start(t);
    pop.stop(t + 0.18);
    click.stop(t + 0.18);
    ctx.resume();
  } catch {
    /* autoplay can be blocked */
  }
}

const QUICK_EMOJIS = ["👋", "✨", "😂", "❤️", "🔥", "👍", "🐱", "💃", "😊", "🙌"];

function hasEmoji(text) {
  return /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(text);
}

function decorateUserReply(text) {
  if (hasEmoji(text)) return text;
  const t = text.toLowerCase();
  if (/(danc|hip\s?hop)/.test(t)) return `${text} 💃`;
  if (/(cat|hike|hiking)/.test(t)) return `${text} 🐱`;
  if (/(love|amazing|great)/.test(t)) return `${text} ✨`;
  if (/(work|design|portfolio)/.test(t)) return `${text} 💼`;
  if (/^(hi|hey|hello|yo)\b/.test(t)) return `${text} 👋`;
  if (/(no|not really)/.test(t)) return `${text} 😅`;
  if (/(yes|yeah|yep|sure)/.test(t)) return `${text} 😊`;
  return `${text} 🙂`;
}

function lastUserIndex(list) {
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].role === "user") return i;
  }
  return -1;
}

function track(name, params = {}) {
  if (window.gtag) window.gtag("event", name, params);
}

function GreetAudioBubble({ url, autoPlay }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !autoPlay) return;
    audio.play().catch(() => {});
  }, [autoPlay, url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [url]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  return (
    <div className="ah-greet-audio">
      <audio ref={audioRef} src={url} preload="auto" playsInline />
      <button
        type="button"
        className="ah-greet-play"
        onClick={togglePlay}
        aria-label={playing ? "Pause voice greeting" : "Play voice greeting"}
      >
        {playing ? "❚❚" : "▶"}
      </button>
      <span className="ah-greet-wave" aria-hidden="true">
        <i className={playing ? "is-live" : ""} />
        <i className={playing ? "is-live" : ""} />
        <i className={playing ? "is-live" : ""} />
        <i className={playing ? "is-live" : ""} />
      </span>
      <span className="ah-greet-label">Voice</span>
    </div>
  );
}

const FIELD_TEXT =
  "The Tao gives birth to One. One gives birth to Two. Two gives birth to Three. Three gives birth to all things. ";

function taijiZone(x, y, R) {
  const d2 = x * x + y * y;
  if (d2 > R * R) return "out";
  const rHalf = R * 0.5;
  const rHalf2 = rHalf * rHalf;
  const eye2 = (R * 0.12) * (R * 0.12);
  const top = x * x + (y + rHalf) * (y + rHalf) <= rHalf2;
  const bot = x * x + (y - rHalf) * (y - rHalf) <= rHalf2;
  if (x * x + (y + rHalf) * (y + rHalf) <= eye2) return "yin-eye";
  if (x * x + (y - rHalf) * (y - rHalf) <= eye2) return "yang-eye";
  if (top || (x < 0 && !bot)) return "yang";
  return "yin";
}

function TaijiInline() {
  return (
    <svg className="ah-taiji-mark" viewBox="0 0 100 100" role="img" aria-label="Yin and yang symbol">
      <circle cx="50" cy="50" r="48" fill="#0c0c0c" stroke="#e10600" strokeWidth="2.5" />
      <path d="M50 2 A48 48 0 0 1 50 98 A24 24 0 0 1 50 50 A24 24 0 0 0 50 2" fill="#e10600" opacity="0.92" />
      <circle cx="50" cy="26" r="7" fill="#0c0c0c" />
      <circle cx="50" cy="74" r="7" fill="#e10600" />
    </svg>
  );
}

function GridField({ avoidRef, highlight, boundsRef, compact = false }) {
  const canvasRef = useRef(null);
  const highlightRef = useRef(false);
  highlightRef.current = Boolean(highlight);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let width = 0;
    let height = 0;
    let stream = 0;
    let angle = 0;
    let shown = compact ? 0.55 : 0;
    let lastActive = performance.now();
    const IDLE_MS = compact ? 1200 : 3500;
    const body = { x: 0, y: 0, vx: 0.32, vy: 0.22 };
    const SPEED = reduced ? 0.15 : compact ? 0.28 : 0.38;

    const markActive = () => {
      lastActive = performance.now();
    };
    const ACTIVITY = ["keydown", "input", "pointerdown", "wheel", "touchstart"];

    const radius = () => Math.min(width, height) * (compact ? 0.34 : 0.2);

    const measure = () => {
      const host = boundsRef?.current;
      if (host) {
        return { width: host.clientWidth, height: host.clientHeight };
      }
      return { width: window.innerWidth, height: window.innerHeight };
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const next = measure();
      width = next.width;
      height = next.height;
      if (width < 2 || height < 2) return;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!body.x && !body.y) {
        body.x = width * 0.55;
        body.y = height * 0.52;
      }
    };

    const bounceOffPanel = (R) => {
      if (compact || boundsRef?.current) return;
      const el = avoidRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const pad = 18;
      const left = r.left - pad;
      const right = r.right + pad;
      const top = r.top - pad;
      const bottom = r.bottom + pad;
      const nx = Math.max(left, Math.min(body.x, right));
      const ny = Math.max(top, Math.min(body.y, bottom));
      let dx = body.x - nx;
      let dy = body.y - ny;
      const dist = Math.hypot(dx, dy);
      if (dist >= R) return;
      if (dist < 0.001) {
        const toL = body.x - left;
        const toR = right - body.x;
        const toT = body.y - top;
        const toB = bottom - body.y;
        const m = Math.min(toL, toR, toT, toB);
        dx = 0;
        dy = 0;
        if (m === toL) dx = -1;
        else if (m === toR) dx = 1;
        else if (m === toT) dy = -1;
        else dy = 1;
        body.x = (dx < 0 ? left : dx > 0 ? right : body.x) + dx * R;
        body.y = (dy < 0 ? top : dy > 0 ? bottom : body.y) + dy * R;
      } else {
        dx /= dist;
        dy /= dist;
        body.x = nx + dx * R;
        body.y = ny + dy * R;
      }
      const dot = body.vx * dx + body.vy * dy;
      if (dot < 0) {
        body.vx -= 2 * dot * dx;
        body.vy -= 2 * dot * dy;
      }
    };

    const move = (R) => {
      const wobble = (Math.random() - 0.5) * 0.004;
      const c = Math.cos(wobble);
      const s = Math.sin(wobble);
      const vx = body.vx * c - body.vy * s;
      const vy = body.vx * s + body.vy * c;
      const mag = Math.hypot(vx, vy) || 1;
      body.vx = (vx / mag) * SPEED;
      body.vy = (vy / mag) * SPEED;
      body.x += body.vx;
      body.y += body.vy;
      if (body.x < R) {
        body.x = R;
        body.vx = Math.abs(body.vx);
      }
      if (body.x > width - R) {
        body.x = width - R;
        body.vx = -Math.abs(body.vx);
      }
      if (body.y < R) {
        body.y = R;
        body.vy = Math.abs(body.vy);
      }
      if (body.y > height - R) {
        body.y = height - R;
        body.vy = -Math.abs(body.vy);
      }
      bounceOffPanel(R);
    };

    const tick = () => {
      ctx.fillStyle = "#e7e7e7";
      ctx.fillRect(0, 0, width, height);

      const R = radius();
      const idle = performance.now() - lastActive > IDLE_MS;
      const wantShown = highlightRef.current ? 1 : idle ? 1 : 0;
      shown += (wantShown - shown) * (highlightRef.current ? 0.14 : idle ? 0.012 : 0.08);
      if (shown < 0.002) shown = 0;
      if (shown > 0) {
        move(R);
        angle += reduced ? 0.002 : 0.006;
        stream += 0.16;
      }

      const tw = 7.2;
      const th = 11;
      const colsT = Math.ceil(width / tw) + 2;
      const rowsT = Math.ceil(height / th) + 2;
      const shift = Math.floor(stream);
      const len = FIELD_TEXT.length;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "500 11px ui-monospace, SFMono-Regular, Menlo, monospace";

      for (let gy = 0; gy < rowsT; gy += 1) {
        for (let gx = 0; gx < colsT; gx += 1) {
          const cellX = gx * tw + tw / 2;
          const cellY = gy * th + th / 2;
          const px = cellX - body.x;
          const py = cellY - body.y;
          const zone =
            shown > 0 ? taijiZone(px * cos + py * sin, -px * sin + py * cos, R) : "out";
          const idx = gy * colsT + gx;
          if (zone !== "out" && shown < 1) {
            ctx.globalAlpha = 1 - shown;
            ctx.fillStyle = "rgba(40,40,40,0.28)";
            ctx.fillText(FIELD_TEXT[idx % len], cellX, cellY);
            ctx.globalAlpha = shown;
          }
          const ch = FIELD_TEXT[(zone === "out" ? idx : idx + shift) % len];
          const hi = highlightRef.current && shown > 0.35;
          if (zone === "yang") {
            ctx.fillStyle = hi ? "rgba(225, 6, 0, 0.62)" : "rgba(28,28,28,0.38)";
            ctx.fillText(ch, cellX, cellY);
          } else if (zone === "yin") {
            ctx.fillStyle = hi ? "rgba(8,8,8,0.96)" : "rgba(8,8,8,0.92)";
            ctx.fillText(ch, cellX, cellY);
            ctx.fillText(ch, cellX + 0.6, cellY + 0.4);
            ctx.fillText(ch, cellX - 0.4, cellY + 0.7);
            if (hi) {
              ctx.fillStyle = "rgba(225, 6, 0, 0.22)";
              ctx.fillText(ch, cellX + 1, cellY + 1);
            }
          } else if (zone === "yin-eye") {
            ctx.fillStyle = hi ? "rgba(225, 6, 0, 0.85)" : "rgba(6,6,6,0.95)";
            ctx.fillText(ch, cellX, cellY);
            ctx.fillText(ch, cellX + 0.8, cellY);
            ctx.fillText(ch, cellX, cellY + 0.8);
          } else if (zone === "yang-eye") {
            ctx.fillStyle = hi ? "rgba(12,12,12,0.9)" : "rgba(40,40,40,0.28)";
            ctx.fillText(ch, cellX, cellY);
          } else if (zone !== "yang-eye") {
            ctx.fillStyle = "rgba(40,40,40,0.28)";
            ctx.fillText(ch, cellX, cellY);
          }
          ctx.globalAlpha = 1;
        }
      }

      raf = requestAnimationFrame(tick);
    };

    resize();
    raf = requestAnimationFrame(tick);
    window.addEventListener("resize", resize);
    ACTIVITY.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));

    let resizeObserver;
    if (boundsRef?.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(boundsRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      ACTIVITY.forEach((evt) => window.removeEventListener(evt, markActive));
      resizeObserver?.disconnect();
    };
  }, [avoidRef, highlight, boundsRef, compact]);

  return (
    <div className={`ah-field${highlight ? " ah-field--highlight" : ""}`} aria-hidden="true">
      <canvas ref={canvasRef} className="ah-ascii" />
    </div>
  );
}

const AgentHome = ({ embedded = false, onClose }) => {
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [greeting, setGreeting] = useState(true);
  const [greetLoading, setGreetLoading] = useState(false);
  const [onboarding, setOnboarding] = useState("name");
  const [visitorName, setVisitorName] = useState("");
  const [visitorRole, setVisitorRole] = useState("");
  const [visitorPurpose, setVisitorPurpose] = useState("");
  const [questions, setQuestions] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sessionEnded, setSessionEnded] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [askFocused, setAskFocused] = useState(false);
  const [taijiHighlight, setTaijiHighlight] = useState(false);

  const inputRef = useRef(null);
  const taijiTimerRef = useRef(null);
  const bottomRef = useRef(null);
  const sessionIdRef = useRef("");
  const onboardingRef = useRef("name");
  const visitorNameRef = useRef("");
  const visitorRoleRef = useRef("");
  const visitorPurposeRef = useRef("");
  const userTurnRef = useRef(0);
  const panelRef = useRef(null);
  const started = messages.some((msg) => msg.role === "user");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/session`, { method: "POST" });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Could not start chat");
        if (cancelled) return;
        sessionIdRef.current = data.sessionId;
        setSessionId(data.sessionId);
        if (data.questions?.length) {
          setQuestions(filterDefaultChips(data.questions).slice(0, 3));
        }
        track("digital_young_start");
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const startId = window.setTimeout(() => {
      setGreetLoading(true);
      window.setTimeout(() => {
        playPop();
        setMessages([
          {
            role: "assistant",
            content: GREET_ASK,
            greetAudio: true,
            audioUrl: GREET_AUDIO,
            showGreetText: false,
            autoPlayGreet: true,
            pop: true,
          },
        ]);
        setGreeting(false);
        setGreetLoading(false);
      }, 480);
    }, 1400);

    return () => window.clearTimeout(startId);
  }, []);

  const toggleGreetText = (index) => {
    setMessages((prev) => {
      const next = prev.slice();
      const row = next[index];
      if (!row?.greetAudio) return prev;
      next[index] = { ...row, showGreetText: !row.showGreetText };
      return next;
    });
  };

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  useEffect(() => {
    if (started) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy, started]);

  const ask = async (raw) => {
    const text = (raw || input).trim();
    if (!text || busy || sessionEnded) return;
    if (onboardingRef.current === "done" && !sessionIdRef.current) return;
    setInput("");
    setBusy(true);
    setError("");
    setFollowups([]);
    userTurnRef.current += 1;
    const turn = userTurnRef.current;
    const plannedReaction = reactionForUserTurn(turn);

    setMessages((prev) => [
      ...prev,
      { role: "user", content: decorateUserReply(text), raw: text, status: "sent" },
    ]);
    setEmojiOpen(false);
    track("digital_young_cta");

    window.setTimeout(() => {
      setMessages((prev) => {
        const i = lastUserIndex(prev);
        if (i < 0 || prev[i].status !== "sent") return prev;
        const next = prev.slice();
        next[i] = { ...next[i], status: "delivered" };
        return next;
      });
    }, 280);
    window.setTimeout(() => {
      setMessages((prev) => {
        const i = lastUserIndex(prev);
        if (i < 0) return prev;
        const next = prev.slice();
        next[i] = { ...next[i], status: "read" };
        return next;
      });
    }, 900);
    window.setTimeout(() => {
      if (!plannedReaction) return;
      setMessages((prev) => {
        const i = lastUserIndex(prev);
        if (i < 0 || prev[i].reaction) return prev;
        const next = prev.slice();
        next[i] = {
          ...next[i],
          reaction: plannedReaction.type,
          reactionShake: plannedReaction.shake,
        };
        return next;
      });
      if (plannedReaction.shake && typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate([12, 36, 12]);
        } catch {
          /* ignored */
        }
      }
    }, 1150);

    const stage = onboardingRef.current;
    let nameAcceptedThisTurn = false;

    if (stage === "name") {
      const givenName = await resolveVisitorName(text, null, messages);
      if (givenName) {
        nameAcceptedThisTurn = true;
        const name = prettyName(givenName);
        visitorNameRef.current = name;
        setVisitorName(name);
        onboardingRef.current = "role";
        setOnboarding("role");
        window.setTimeout(() => {
          playPop();
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: `Nice to meet you, ${name} ✨ What do you do: design, hiring, eng/PM, student, or something else?`,
              pop: true,
            },
          ]);
          setFollowups(ROLE_CHIPS.slice(0, 3));
          setBusy(false);
          setTimeout(() => inputRef.current?.focus(), 40);
        }, 1200);
        return;
      }
      onboardingRef.current = "done";
      setOnboarding("done");
    }

    if (stage === "role") {
      visitorRoleRef.current = text;
      setVisitorRole(text);
      onboardingRef.current = "purpose";
      setOnboarding("purpose");
      window.setTimeout(() => {
        playPop();
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Got it 🙂 What brought you to Young's portfolio today?",
            pop: true,
          },
        ]);
        setFollowups(PURPOSE_CHIPS);
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 40);
      }, 1200);
      return;
    }

    if (stage === "purpose") {
      visitorPurposeRef.current = text;
      setVisitorPurpose(text);
      onboardingRef.current = "done";
      setOnboarding("done");
    }

    try {
      if (!sessionIdRef.current) throw new Error("Chat is still connecting. Try again in a second.");
      const history = [
        ...messages.slice(-7),
        { role: "user", content: text },
      ].map((m) => ({ role: m.role, content: m.raw || m.content }));
      let context = buildVisitorContext({
        name: visitorNameRef.current,
        role: visitorRoleRef.current,
        purpose: visitorPurposeRef.current,
      });
      if (stage === "name" && !nameAcceptedThisTurn && !visitorNameRef.current) {
        context = [
          context,
          "Your greeting asked how they are, not their name. Their reply is small talk or not a name; respond naturally. Do not call them by a word from that reply. Ask for their name only if it feels natural, one short question.",
        ]
          .filter(Boolean)
          .join(" ");
      }
      if (stage === "purpose") {
        context = [
          context,
          "They just shared why they're here. Briefly acknowledge (1 sentence), then answer helpfully for that goal. Offer one relevant follow-up they might ask next.",
        ]
          .filter(Boolean)
          .join(" ");
      }
      const res = await fetch(`${API_URL}/api/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          text,
          context,
          history,
        }),
      });
      const data = await res.json();
      if (res.status === 429 && data.turnsLeft === 0) {
        setSessionEnded(true);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.boundary || "That's a good place to meet the real Young 👋" },
        ]);
        return;
      }
      if (!res.ok) throw new Error(data.error || "Couldn't answer.");

      await deliverAssistantBubbles(data, { setMessages, setTaijiHighlight, taijiTimerRef });
      setFollowups(data.suggested_followups || []);
      track("digital_young_turn");
    } catch (err) {
      setError(err.message);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I lost the connection. Try again, or open the portfolio." },
      ]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  };

  const chips = followups.length
    ? followups
    : onboarding === "role"
      ? ROLE_CHIPS.slice(0, 3)
      : onboarding === "purpose"
        ? PURPOSE_CHIPS
        : filterDefaultChips(questions).slice(0, 3);
  const lastUser = lastUserIndex(messages);
  const receiptLabel = (status) => {
    if (busy && status !== "sent") return "Read";
    if (status === "read") return "Read";
    if (status === "delivered") return "Delivered";
    return "Sent";
  };

  const chatPanelCore = (
      <div className={`ah-chat-panel${embedded ? " ah-chat-panel--embedded" : ""}`} ref={panelRef}>
          {!embedded && (
            <header className="ah-embedded-head">
              <div>
                <p className="ah-embedded-kicker">Digital Young</p>
                <p className="ah-embedded-sub">
                  <span className="ah-embedded-live" aria-hidden="true" />
                  AI portfolio guide · online
                </p>
              </div>
            </header>
          )}
          <div className="ah-thread" role="log" aria-live="polite">
            {messages.map((msg, i) => (
              <div key={i} className={`ah-msg ah-msg-${msg.role}${msg.pop ? " ah-msg-pop" : ""}`}>
                {msg.role === "assistant" && <span className="ah-msg-orb" aria-hidden="true" />}
                <div className="ah-msg-stack">
                  {msg.greetAudio ? (
                    <>
                      <div className="ah-greet-row">
                        <div className="ah-msg-body ah-greet-bubble">
                          <GreetAudioBubble
                            url={msg.audioUrl || GREET_AUDIO}
                            autoPlay={Boolean(msg.autoPlayGreet)}
                          />
                        </div>
                        <button
                          type="button"
                          className="ah-greet-text-btn"
                          onClick={() => toggleGreetText(i)}
                          aria-pressed={Boolean(msg.showGreetText)}
                          aria-label={msg.showGreetText ? "Hide text transcript" : "Show text transcript"}
                          title={msg.showGreetText ? "Hide text" : "Show text"}
                        >
                          Aa
                        </button>
                      </div>
                      {msg.showGreetText && (
                        <div className="ah-msg-body">
                          <p>{msg.content}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="ah-msg-body">
                      {msg.reaction === "like" && (
                        <span className="ah-msg-react" role="img" aria-label="Young liked this">
                          👍
                        </span>
                      )}
                      {msg.reaction === "heart" && (
                        <span
                          className={`ah-msg-react${msg.reactionShake ? " ah-msg-react--shake" : ""}`}
                          role="img"
                          aria-label="Young loved this"
                        >
                          ❤️
                        </span>
                      )}
                      <p>{msg.content}</p>
                      {msg.showTaiji && <TaijiInline />}
                      {msg.image && <img src={msg.image} alt={msg.imageAlt || ""} className="ah-msg-pic" />}
                    </div>
                  )}
                  {msg.role === "user" && i === lastUser && (
                    <p className="ah-msg-receipt">{receiptLabel(msg.status)}</p>
                  )}
                </div>
              </div>
            ))}
            {(greeting || greetLoading) && (
              <div className="ah-msg ah-msg-assistant ah-msg-typing-row">
                <div className="ah-msg-body ah-msg-speaking" aria-label="Young is speaking" role="status">
                  <span className="ah-speaking-text">Young is speaking</span>
                  <span className="ah-speaking-bars" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
              </div>
            )}
            {busy && !greetLoading && (
              <div className="ah-msg ah-msg-assistant ah-msg-typing-row">
                <div className="ah-msg-body ah-msg-typing" aria-label="Young is typing" role="status">
                  <span className="ah-typing-dots">
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
              </div>
            )}
            {chips.length > 0 && (
              <div className="ah-chips ah-chips--dock">
                {chips.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => ask(s)}
                    disabled={busy || greeting || greetLoading || sessionEnded || (onboarding === "done" && !sessionId)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {error && <p className="ah-error">{error}</p>}

          <div className="ah-composer">
            {emojiOpen && (
              <div className="ah-emoji-tray" role="listbox" aria-label="Emojis">
                {QUICK_EMOJIS.map((emo) => (
                  <button
                    key={emo}
                    type="button"
                    onClick={() => {
                      setInput((v) => `${v}${emo}`);
                      inputRef.current?.focus();
                    }}
                  >
                    {emo}
                  </button>
                ))}
              </div>
            )}
            <form
              className="ah-ask ah-ask--dock"
              onSubmit={(e) => {
                e.preventDefault();
                ask();
              }}
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => setAskFocused(true)}
                onBlur={() => setAskFocused(false)}
                placeholder={
                  sessionEnded
                    ? "Chat complete"
                    : "Say hi, share your name, or ask a question..."
                }
                aria-label="Message Digital Young"
                disabled={busy || greeting || greetLoading || sessionEnded || (onboarding === "done" && !sessionId)}
              />
              <FoxGrid hidden={askFocused || Boolean(input.trim()) || emojiOpen || busy} />
              <div className="ah-ask-bar">
                <button
                  type="button"
                  className="ah-ask-plus"
                  aria-label="Add emoji"
                  aria-expanded={emojiOpen}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setEmojiOpen((open) => !open)}
                  disabled={busy || greeting || greetLoading || sessionEnded}
                >
                  +
                </button>
                <button
                  type="submit"
                  aria-label="Send"
                  disabled={
                    !input.trim() ||
                    busy ||
                    greeting ||
                    greetLoading ||
                    sessionEnded ||
                    (onboarding === "done" && !sessionId)
                  }
                >
                  ↑
                </button>
              </div>
            </form>
          </div>
        </div>
  );

  const chatPanel = embedded ? (
    chatPanelCore
  ) : (
    <div className="ah-stage-copy">{chatPanelCore}</div>
  );

  if (embedded) {
    return (
      <div className={`ah-embedded${started ? " ah-embedded--started" : ""}`}>
        <header className="ah-embedded-head">
          <div>
            <p className="ah-embedded-kicker">Digital Young</p>
            <p className="ah-embedded-sub">
              <span className="ah-embedded-live" aria-hidden="true" />
              AI portfolio guide · online
            </p>
          </div>
          <div className="ah-embedded-actions">
            <Link to="/agent" className="ah-embedded-full" onClick={onClose}>
              Full page
            </Link>
            <button type="button" className="ah-embedded-close" onClick={onClose} aria-label="Close chat">
              ×
            </button>
          </div>
        </header>
        {chatPanel}
      </div>
    );
  }

  return (
    <div className={`ah-page${started ? " ah-page--started" : ""}`}>
      <header className="ah-chat-top">
        <Link className="ah-back" to="/">
          ← Back
        </Link>
        <nav className="ah-top-nav">
          <a className="ah-chat-link" href="/">
            Work
          </a>
          <Link className="ah-chat-link" to="/resume">
            Resume
          </Link>
        </nav>
      </header>
      <GridField avoidRef={panelRef} highlight={taijiHighlight} />
      {chatPanel}
    </div>
  );
};

export default AgentHome;
